/**
 * CoSingery — **播放清單（分類）目錄** —— ⭐ **唯一來源**（`decisions/033`）
 *
 * 產品負責人 2026-09-24：
 *   「i want the player tap, insid will have catagory, different feeling such as happy, sad,
 *    exciting, celebration, to my love, try to think of some catagory for me」
 *   ＋「one more button beside sharing, name it add to playlist, then pop up the catagory to add in」
 *
 * 設計（為何要一個檔）：
 *   · **一個 slug 清單，前後端共用** —— 後端 lazy 建立系統分類（`song_playlists.slug`）、前端顯示
 *     （i18n label）。兩邊各寫一份 = 一定會走樣（加一個分類就要改兩處，遲早漏）。
 *   · ⚠️ 本檔**只可以係純資料**（唔准 import react／i18next）：Worker（`functions/_lib/song/playlists.ts`）
 *     直接讀呢個檔（先例：`functions/_lib/safety/crisis-mode.ts` 讀 `src/locales/*`）。
 *   · **顯示文字（label）同資料（slug）分開**：`labelKey` 交畀 UI 用 `t()` 解 → 三語自動跟。
 *
 * ⚠️ 改呢個清單 ＝ 改產品嘅分類（要產品負責人批；`decisions/033`）。
 */

export interface PlaylistDef {
  /** 穩定 id（入 DB `song_playlists.slug`；**唔准改**，改 ＝ 舊資料變孤兒） */
  slug: string;
  /** i18n key（三語字典要有；`src/locales/*`） */
  labelKey: string;
  /** ⭐ 顯示次序（唔傳 ＝ 跟目錄 index）；用 −1 可以令某個分類排最前（例：`favourite`） */
  sort?: number;
}

/**
 * 系統分類（產品負責人 2026-09-24 確認 12 個 ＋ ⭐ 2026-09-24#2 加 **`favourite`（我的最愛）**
 * —— 佢要求播放器要有「add to my favourite」基本功能）
 * 次序 ＝ 畫面顯示次序（`sort` 唔傳就係 index）
 */
export const SYSTEM_PLAYLISTS: PlaylistDef[] = [
  /** ⭐ 排最前（`sort: -1`）—— 一撳 ♥ 就入呢個 */
  { slug: 'favourite', labelKey: 'pl_favourite', sort: -1 },
  { slug: 'happy', labelKey: 'pl_happy' },
  { slug: 'sad', labelKey: 'pl_sad' },
  { slug: 'excited', labelKey: 'pl_excited' },
  { slug: 'celebrate', labelKey: 'pl_celebrate' },
  { slug: 'to_my_love', labelKey: 'pl_to_my_love' },
  { slug: 'missing', labelKey: 'pl_missing' },
  { slug: 'gratitude', labelKey: 'pl_gratitude' },
  { slug: 'healing', labelKey: 'pl_healing' },
  { slug: 'cheer', labelKey: 'pl_cheer' },
  { slug: 'proud', labelKey: 'pl_proud' },
  { slug: 'angry', labelKey: 'pl_angry' },
  { slug: 'fun', labelKey: 'pl_fun' },
];

/** ⭐ 「我的最愛」（♥ 一撳就入）—— 單一常數，唔准喺 UI 寫死字串 */
export const FAVOURITE_SLUG = 'favourite';

export const SYSTEM_PLAYLIST_SLUGS: readonly string[] = SYSTEM_PLAYLISTS.map((p) => p.slug);

/** 係唔係系統分類（用戶**唔可以**改名／刪除） */
export function isSystemSlug(slug: string | null | undefined): boolean {
  return typeof slug === 'string' && SYSTEM_PLAYLIST_SLUGS.includes(slug);
}

/** 自訂清單嘅 slug 前綴（`song_playlists(slug)` 有 unique index → 每個自訂清單要獨一） */
export const CUSTOM_SLUG_PREFIX = 'custom:';

/** 用戶自己開嘅清單（顯示名存 `song_playlists.name`） */
export function isCustomSlug(slug: string | null | undefined): boolean {
  return typeof slug === 'string' && slug.startsWith(CUSTOM_SLUG_PREFIX);
}

/** 由 slug 攞 i18n key（唔係系統分類 → `null`，由呼叫者用 `name` 顯示） */
export function systemLabelKey(slug: string | null | undefined): string | null {
  const hit = SYSTEM_PLAYLISTS.find((p) => p.slug === slug);
  return hit ? hit.labelKey : null;
}

export interface PlaylistLabelSource {
  slug: string;
  name: string | null;
}

/**
 * 清單顯示名（**唯一來源**；三個畫面共用：加入彈窗／播放器分類／日記）
 *   · 系統分類 → i18n（跟介面語言；`t` 由呼叫者傳入 —— 本檔唔准 import i18next）
 *   · 自訂清單 → 用戶自己打嘅名
 */
export function playlistLabel(pl: PlaylistLabelSource, t: (key: string) => string): string {
  const key = systemLabelKey(pl.slug);
  if (key) return t(key);
  const name = (pl.name ?? '').trim();
  return name || t('playlist_untitled');
}
